import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { RemixHandoffHoldReleaseReason } from '@atlassian/malleable-ui-components/state/machine/remix-handoff-hold';
import type { EditorRemixLifecycleEndReason } from '@atlassian/malleable-ui-components/types';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import type { ObjectSidebarPageRemixStartRejectionReason } from './ObjectSidebarPageRemixStart.ts';

export type ObjectSidebarRemixLifecycleDiagnosticAction = 'failed' | 'observed' | 'rejected';

export type ObjectSidebarRemixLifecycleOperation =
	| 'acceptedRejected'
	| 'duplicateTerminalAttempt'
	| 'endRemix'
	| 'handoffHoldReleased'
	| 'insertedRejected'
	| 'insertionAcknowledgementAfterSettlement'
	| 'insertionNotAcknowledged'
	| 'machineReadMismatch'
	| 'ownerDestroyedWithActiveFlow'
	| 'preparePageRemixStart'
	| 'requestRovoPageRemix'
	| 'sendCancelled'
	| 'sendTransition'
	| 'stagingTransferSettled'
	| 'stagingTransferSuperseded'
	| 'staleStagingTransferCallback'
	| 'startRemix'
	| 'validateAdmissionIdentity'
	| 'validateRovoSubmitScope';

/** Which staging-transfer callback fired for a transfer that is no longer the pending one. */
export type ObjectSidebarStagingTransferCallbackName =
	| 'previewOpened'
	| 'submitted'
	| 'previewDismissed'
	| 'insertionAcknowledged'
	| 'insertionNotAcknowledged';

export type ObjectSidebarRemixLifecycleEntryPath =
	| 'editor'
	| 'popup-open'
	| 'popup-option'
	| 'unknown';

/**
 * Bounded attributes only. The ticket forbids prompt, ADF, URL, message content,
 * conversation ID, Remix ID, or any other raw identifier in this event.
 */
export type ObjectSidebarRemixLifecycleDiagnosticAttributes = {
	operation: ObjectSidebarRemixLifecycleOperation;
	entryPath?: ObjectSidebarRemixLifecycleEntryPath;
	errorName?: string;
	outcome?:
		| 'timed-out-in-flight'
		| 'timed-out-cancelled'
		| 'rejected'
		| 'handed-off'
		| 'inserted'
		| 'cancelled'
		| 'abandoned';
	rejectionReason?: ObjectSidebarPageRemixStartRejectionReason;
	terminalReason?: EditorRemixLifecycleEndReason;
	transferPending?: true;
	callback?: ObjectSidebarStagingTransferCallbackName;
	/** How a hold that kept Remix in progress after a Rovo Chat handoff ended. */
	holdReleaseReason?: RemixHandoffHoldReleaseReason;
};

export type ReportObjectSidebarRemixLifecycleDiagnostic = (
	action: ObjectSidebarRemixLifecycleDiagnosticAction,
	attributes: ObjectSidebarRemixLifecycleDiagnosticAttributes,
) => void;

/**
 * Single emitter for `objectSidebarRemixLifecycleOperation` operational events. Adds the
 * current page mode so rollout validation can split renderer/view from editor. Analytics
 * construction or fire failures are swallowed: diagnostics must never alter lifecycle
 * behaviour.
 */
export const useObjectSidebarRemixLifecycleDiagnostics =
	(): ReportObjectSidebarRemixLifecycleDiagnostic => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		// Bounded enum: 'view' | 'edit' | 'live'.
		const pageMode = useGetPageMode();

		return useCallback(
			(
				action: ObjectSidebarRemixLifecycleDiagnosticAction,
				attributes: ObjectSidebarRemixLifecycleDiagnosticAttributes,
			) => {
				try {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action,
							actionSubject: 'objectSidebarRemixLifecycleOperation',
							source: 'objectSidebarRemix',
							attributes: { ...attributes, pageMode },
						},
					}).fire();
				} catch {
					// Diagnostics must never affect lifecycle behavior.
				}
			},
			[createAnalyticsEvent, pageMode],
		);
	};
